<script setup lang="ts">
import { ISidebarItem } from '@/interfaces/components/sidebar/sidebarItem'
import { useSidebarStore } from '@/store/sidebar'
import {
  BellAlertIcon,
  CalculatorIcon,
  ChartBarSquareIcon,
  ClipboardDocumentListIcon,
  CpuChipIcon,
  HomeIcon,
  RectangleGroupIcon,
  Squares2X2Icon,
  TableCellsIcon,
  WrenchScrewdriverIcon,
  DocumentChartBarIcon,
  BuildingOffice2Icon,
} from '@heroicons/vue/24/outline'
import { computed, watch } from 'vue'
import { useRoute } from 'vue-router'

import { logFirebaseAnalytics } from '@/utils/firebase'
import SidebarDropdown from './SidebarDropdown.vue'

const route = useRoute()
const sidebarStore = useSidebarStore()
const props = withDefaults(
  defineProps<{
    item: ISidebarItem
    collapsed?: boolean
  }>(),
  {
    collapsed: false,
  },
)

const iconMap = {
  dashboard: Squares2X2Icon,
  groups: RectangleGroupIcon, // grupos de sensores (não pessoas)
  report: ClipboardDocumentListIcon,
  sensors: CpuChipIcon, // sensores/IoT
  registers: TableCellsIcon,
  rate: ChartBarSquareIcon,
  alarms: BellAlertIcon,
  maintenances: WrenchScrewdriverIcon, // manutenção/ferramentas
  optimizer: CalculatorIcon, // otimizador de tarifa
  anomaly_detector: DocumentChartBarIcon, // detecção de anomalias
  management: BuildingOffice2Icon, // módulo gerencial (áreas da empresa)
}

const hasChildren = computed(() => !!props.item.children?.length)
const iconComponent = computed(
  () => iconMap[props.item.iconName as keyof typeof iconMap] || HomeIcon,
)

const isRouteMatch = (item: ISidebarItem) => {
  if (route.path === item.route || route.path.startsWith(`${item.route}/`)) {
    return true
  }

  return (item.activeMatchers || []).some((matcher) => route.path.startsWith(matcher))
}

const isChildrenActive = computed(() =>
  (props.item.children || []).some((child) => isRouteMatch(child)),
)

const isItemActive = computed(() => {
  if (hasChildren.value) {
    return isChildrenActive.value
  }

  return isRouteMatch(props.item)
})

const isGroupOpen = computed(
  () => !props.collapsed && sidebarStore.expandedGroup === props.item.label,
)

watch(
  () => route.path,
  () => {
    if (hasChildren.value && isChildrenActive.value) {
      sidebarStore.setExpandedGroup(props.item.label)
    }
  },
  { immediate: true },
)

const handleItemClick = () => {
  logFirebaseAnalytics(`click-${props.item.route}`)
  if (hasChildren.value) {
    if (props.collapsed) {
      sidebarStore.setCollapsed(false)
      sidebarStore.setExpandedGroup(props.item.label)
      return
    }

    sidebarStore.setExpandedGroup(isGroupOpen.value ? null : props.item.label)
    return
  }

  if (window.innerWidth < 1024) {
    sidebarStore.closeSidebar()
  }
}
</script>

<template>
  <li>
    <router-link
      :to="hasChildren ? '#' : item.route"
      class="group relative flex items-center gap-2.5 font-medium text-bodydark1 duration-300 ease-in-out hover:bg-blue dark:hover:bg-meta-4"
      :class="[
        collapsed ? 'justify-center px-0 py-2.5' : 'px-4 py-2',
        { 'bg-blue text-white': isItemActive },
      ]"
      :style="{ minHeight: '44px' }"
      :title="collapsed ? item.label : ''"
      @click.prevent="handleItemClick">
      <component
        :is="iconComponent"
        class="min-h-5 min-w-5"
        :class="collapsed ? 'h-6 w-6' : 'h-5 w-5'" />
      <span v-if="!collapsed">{{ item.label }}</span>
      <svg
        v-if="hasChildren && !collapsed"
        class="absolute right-4 top-1/2 -translate-y-1/2 fill-current"
        :class="{ 'rotate-180': isGroupOpen }"
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg">
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M4.41107 6.9107C4.73651 6.58527 5.26414 6.58527 5.58958 6.9107L10.0003 11.3214L14.4111 6.91071C14.7365 6.58527 15.2641 6.58527 15.5896 6.91071C15.915 7.23614 15.915 7.76378 15.5896 8.08922L10.5896 13.0892C10.2641 13.4147 9.73651 13.4147 9.41107 13.0892L4.41107 8.08922C4.08563 7.76378 4.08563 7.23614 4.41107 6.9107Z"
          fill="" />
      </svg>
    </router-link>

    <div class="translate transform overflow-hidden" v-show="isGroupOpen">
      <SidebarDropdown v-if="item.children" :items="item.children" />
    </div>
  </li>
</template>
